/**
 * RegionalSalesAdSpend — "Ad Spend by Platform" card: stacked share
 * bar plus per-platform spend / conversions / CPA rows over the
 * trailing 30 days, region-targeted (geo_ad_performance cube).
 *
 * Live when the DTO ships `adSpend`; otherwise the design platforms
 * render as an em-dash skeleton behind <MockedOverlay> (BON-138).
 */
import { MockedOverlay } from '../primitives';

import type { RegionalSalesDto } from './regionalApi';

const EM_DASH = '—';

/** Raw warehouse platform keys → display label. Unknown keys pass through. */
const PLATFORM_LABELS: Record<string, string> = {
  meta: 'Meta (IG/FB)',
  google_ads: 'Google Ads',
  google: 'Google Ads',
  microsoft_ads: 'Microsoft Ads',
  tiktok: 'TikTok',
};

const PLATFORM_COLORS: Record<string, string> = {
  meta: 'var(--nb-warn)',
  google_ads: 'var(--nb-primary)',
  google: 'var(--nb-primary)',
  microsoft_ads: 'var(--nb-info)',
  tiktok: 'var(--nb-info)',
};

const FALLBACK_COLORS = [
  'var(--nb-warn)',
  'var(--nb-primary)',
  'var(--nb-info)',
  'var(--nb-subtle)',
  'var(--nb-forest)',
];

function platformLabel(raw: string): string {
  return PLATFORM_LABELS[raw.toLowerCase()] ?? raw;
}

function platformColor(raw: string, index: number): string {
  return PLATFORM_COLORS[raw.toLowerCase()] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

// Layout-only skeleton: design platforms with the design's share split.
const SKELETON_PLATFORMS = [
  { platform: 'Meta (IG/FB)', color: 'var(--nb-warn)', share: 52 },
  { platform: 'Google Search', color: 'var(--nb-primary)', share: 22 },
  { platform: 'TikTok', color: 'var(--nb-info)', share: 12 },
  { platform: 'Reddit', color: 'var(--nb-subtle)', share: 8 },
  { platform: 'Spotify', color: 'var(--nb-forest)', share: 6 },
] as const;

function Row({
  label,
  color,
  spend,
  conversions,
  cpa,
}: {
  label: string;
  color: string;
  spend: string;
  conversions: string;
  cpa: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <i style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
      <span style={{ flex: 1, fontSize: 12, fontWeight: 500 }}>{label}</span>
      <span
        className="nb-tabular nb-mono"
        style={{ fontSize: 11.5, color: 'var(--nb-muted)', minWidth: 56, textAlign: 'right' }}
      >
        {spend}
      </span>
      <span
        className="nb-tabular nb-mono"
        style={{ fontSize: 11.5, color: 'var(--nb-ink)', minWidth: 40, textAlign: 'right' }}
      >
        {conversions}
      </span>
      <span
        className="nb-tabular nb-mono"
        style={{ fontSize: 11, color: 'var(--nb-subtle)', minWidth: 38, textAlign: 'right' }}
      >
        {cpa} CPA
      </span>
    </div>
  );
}

function ShareBar({
  segments,
}: {
  segments: ReadonlyArray<{ key: string; pct: number; color: string }>;
}) {
  return (
    <div
      style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}
    >
      {segments.map((s) => (
        <div key={s.key} title={s.key} style={{ width: `${s.pct}%`, background: s.color }} />
      ))}
    </div>
  );
}

function LiveBody({ adSpend }: { adSpend: NonNullable<RegionalSalesDto['adSpend']> }) {
  if (adSpend.platforms.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
        No region-targeted ad spend in the last 30 days.
      </div>
    );
  }
  return (
    <>
      <ShareBar
        segments={adSpend.platforms.map((p, i) => ({
          key: platformLabel(p.platform),
          pct: p.sharePct,
          color: platformColor(p.platform, i),
        }))}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {adSpend.platforms.map((p, i) => (
          <Row
            key={p.platform}
            label={platformLabel(p.platform)}
            color={platformColor(p.platform, i)}
            spend={dollars(p.spendCents)}
            conversions={String(p.conversions)}
            cpa={p.cpaCents === null ? EM_DASH : dollars(p.cpaCents)}
          />
        ))}
      </div>
    </>
  );
}

export function RegionalSalesAdSpend({ adSpend }: { adSpend: RegionalSalesDto['adSpend'] }) {
  return (
    <section className="nb-card">
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '0.04em', margin: 0 }}>
          Ad Spend by Platform
        </h3>
        <span className="nb-eyebrow">30 days</span>
      </div>
      {adSpend ? (
        <LiveBody adSpend={adSpend} />
      ) : (
        <MockedOverlay reason="Region-targeted ad spend source is unavailable. Tracked in BON-138.">
          <ShareBar
            segments={SKELETON_PLATFORMS.map((p) => ({
              key: p.platform,
              pct: p.share,
              color: p.color,
            }))}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SKELETON_PLATFORMS.map((p) => (
              <Row
                key={p.platform}
                label={p.platform}
                color={p.color}
                spend={EM_DASH}
                conversions={EM_DASH}
                cpa={EM_DASH}
              />
            ))}
          </div>
        </MockedOverlay>
      )}
    </section>
  );
}
